import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import {
    useMediaQuery,
    useTheme,
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    Select,
    MenuItem,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';


const Supplier = ({ suppliers, selectedSuppliers, onSupplierChange }) => {
    const theme = useTheme();
    const neutralDark = theme.palette.neutral.dark;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [open, setOpen] = useState(false);
    const [currentSupplier, setCurrentSupplier] = useState(null);
    const [formData, setFormData] = useState({ supplierId: '', purchasePrice: 0, currency: '' });

    const handleOpen = (supplier = null) => {
        setCurrentSupplier(supplier);
        setFormData(
            supplier
                ? { 
                    supplierId: supplier.supplierId, 
                    currency: supplier.currency, 
                    purchasePrice: supplier.purchasePrice, 
                    domesticPurchasePrice: supplier.domesticPurchasePrice, 
                    internationalPurchasePrice: supplier.internationalPurchasePrice, 
                }
                : { 
                    supplierId: '', 
                    currency: 'VND', // Default currency
                    purchasePrice: 0, 
                    domesticPurchasePrice: 0, 
                    internationalPurchasePrice: 0, 
                } 
        );
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleSave = () => {
        const supplierData = suppliers.find((supplier) => supplier._id === formData.supplierId) || {};
        
        // Check if supplier already exists in the selectedSuppliers list
        const isExistingSupplier = selectedSuppliers.some((supplier) => supplier.supplierId === formData.supplierId);

        if (!currentSupplier && isExistingSupplier) {
            toast.error('This supplier has already been added!');
            return;
        }

        const updatedSuppliers = currentSupplier
            ? selectedSuppliers.map((supplier) =>
                supplier.supplierId === currentSupplier.supplierId
                    ? { 
                        ...supplier, 
                        currency: formData.currency, 
                        purchasePrice: formData.purchasePrice, 
                        domesticPurchasePrice: formData.domesticPurchasePrice,
                        internationalPurchasePrice: formData.internationalPurchasePrice,
                    }
                    : supplier
            )
            : [
                ...selectedSuppliers,
                {
                    supplierId: formData.supplierId,
                    supplierName: supplierData.supplierName || '',
                    currency: formData.currency,
                    purchasePrice: formData.purchasePrice,
                    domesticPurchasePrice: formData.domesticPurchasePrice,
                    internationalPurchasePrice: formData.internationalPurchasePrice,
                },
            ];
        onSupplierChange(updatedSuppliers);
        handleClose();
    };

    const handleDelete = (supplierId) => {
        onSupplierChange(selectedSuppliers.filter((supplier) => supplier.supplierId !== supplierId));
    };

    return (
        <Box>
            {/* <Typography variant="h6" gutterBottom>Suppliers</Typography> */}
            <Button variant="outlined" onClick={() => handleOpen()} sx={{ mb: 2 }}>
                Add Supplier
            </Button>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Currency</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Domestic Price</TableCell>
                        <TableCell>International Price</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {selectedSuppliers.map((sup) => (
                        <TableRow key={sup.supplierId}>
                        <TableCell>{sup.supplierName}</TableCell>
                        <TableCell>{sup.currency}</TableCell>
                        <TableCell>{sup.purchasePrice}</TableCell>
                        <TableCell>{sup.domesticPurchasePrice}</TableCell>
                        <TableCell>{sup.internationalPurchasePrice}</TableCell>
                        <TableCell>
                            <IconButton onClick={() => handleOpen(sup)}>
                                <Edit />
                            </IconButton>
                            <IconButton onClick={() => handleDelete(sup.supplierId)}>
                                <Delete />
                            </IconButton>
                        </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog
                open={open}
                onClose={handleClose}
                sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '90%' : '36%' } }}
            >
                <DialogTitle borderBottom={`1px solid ${neutralDark}`}>                
                    <Typography>
                        {currentSupplier ? 'Edit Supplier' : 'Add Supplier'}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Select
                        fullWidth
                        value={formData.supplierId}
                        onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                        sx={{ mt: 2 }}
                        disabled={!!currentSupplier}
                    >
                        {suppliers.map((supplier) => (
                            <MenuItem key={supplier._id} value={supplier._id}>
                                {supplier.supplierName}
                            </MenuItem>
                        ))}
                    </Select>
                    <TextField
                        label="Currency"
                        name="currency"
                        fullWidth
                        select
                        variant="filled"
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        sx={{ mt: 2 }}
                    >
                        {['VND', 'USD', 'EUR', 'AUD', 'GBP', 'JPY', 'SGD', 'CHF', 'CNY', 'KRW', 'RUB'].map((unit) => (
                            <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Purchase Price"
                        type="number"
                        fullWidth
                        variant="filled"
                        value={formData.purchasePrice}
                        onChange={(e) => setFormData({ ...formData, purchasePrice: parseFloat(e.target.value) || 0 })}
                        sx={{ mt: 2 }}
                        inputProps={{ min: 0 }}
                    />
                    <TextField
                        label="Domestic Purchase Price"
                        type="number"
                        fullWidth
                        variant="filled"
                        value={formData.domesticPurchasePrice}
                        onChange={(e) => setFormData({ ...formData, domesticPurchasePrice: parseFloat(e.target.value) || 0 })}
                        sx={{ mt: 2 }}
                        inputProps={{ min: 0 }}
                    />
                    <TextField
                        label="International Purchase Price"
                        type="number"
                        fullWidth
                        variant="filled"
                        value={formData.internationalPurchasePrice}
                        onChange={(e) => setFormData({ ...formData, internationalPurchasePrice: parseFloat(e.target.value) || 0 })}
                        sx={{ mt: 2 }}
                        inputProps={{ min: 0 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button 
                        variant="outlined"
                        color="secondary"
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="outlined"
                        onClick={handleSave} 
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Supplier;